import { useCallback, useEffect, useState } from 'react';

export function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function writeStored<T>(key: string, value: T | null | undefined) {
  try {
    if (value === undefined || value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
  }
}

export function useStored<T>(key: string, fallback: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => readStored(key, fallback));
  const set = useCallback((n: T) => { setV(n); writeStored(key, n); }, [key]);
  return [v, set];
}

export type Theme = 'dark' | 'light';
export type Density = 'comfortable' | 'compact';

export function useTheme(): [Theme, (t: Theme) => void] {
  const sys = (): Theme => (window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark');
  const [picked, setPicked] = useStored<Theme | null>('vs.admin.theme', null);
  const [system, setSystem] = useState<Theme>(sys);
  useEffect(() => {
    const m = window.matchMedia?.('(prefers-color-scheme: light)');
    const on = () => setSystem(sys());
    m?.addEventListener('change', on);
    return () => m?.removeEventListener('change', on);
  }, []);
  const theme = picked ?? system;
  useEffect(() => { document.documentElement.setAttribute('data-theme', theme); }, [theme]);
  return [theme, setPicked];
}

export const tokenKey = (env: string) => `vs.admin.token.${env}`;
export function getToken(env: string): string | null {
  return readStored<string | null>(tokenKey(env), null);
}
export function setToken(env: string, token: string | null) {
  writeStored(tokenKey(env), token);
}
